/**
 * 管理后台「用户与收入」三页文案（韩、中、英）：用户与计费、充值订单、啟用码。
 *
 * 背景：页面按 Alice 的版式复刻，文案保持 Alice 的措辞，品牌与数据来源按本仓适配：
 *   「糖豆」改称花瓣，「微信」改称 Stripe，用户就是账号。
 * 设计意图：按页面分组；后端新增的错误码在这里映射（common.ts 的错误表归共用文件，不在本页改），
 *   没映射到的回落到共用表。批量调账每行的错误码（BATCH）也在这里。
 * 关键约束：韩文用 해요체；中文不用破折号和英文引号；不放 emoji。
 */
import { errorText as baseErrorText } from './common.ts'
import { translate, type Entry, type Locale } from './core.tsx'

const e = (zh: string, ko: string, en: string): Entry => ({ zh, ko, en })

const CODES: Record<string, Entry> = {
  code_invalid: e('啟用碼無效或已不可用', '활성화 코드가 올바르지 않거나 더는 쓸 수 없어요', 'Activation code is invalid or no longer available'),
  code_expired: e('啟用碼已過期', '활성화 코드가 만료됐어요', 'Activation code has expired'),
  already_redeemed: e('這個帳號已經兌換過這個啟用碼', '이 계정은 이미 이 코드를 사용했어요', 'This account already redeemed the code'),
  redeem_blocked: e('兌換失敗次數過多，暫時不能兌換', '실패 횟수가 많아 잠시 사용할 수 없어요', 'Too many failed attempts; redemption is locked for now'),
  account_banned: e('帳號已被封禁，不能兌換', '정지된 계정이라 사용할 수 없어요', 'This account is banned and cannot redeem'),
  code_exists: e('這個碼值已經存在', '이미 있는 코드예요', 'This code already exists'),
  invalid_code: e('碼值只能用大寫字母和數字，4 到 32 位', '코드는 대문자와 숫자 4~32자로 만들어 주세요', 'Use uppercase letters and digits, 4 to 32 characters'),
  invalid_count: e('生成數量要在 1 到 10000 之間', '생성 수량은 1~10000이어야 해요', 'Count must be between 1 and 10000'),
  invalid_limit: e('次數上限不合法：單帳號限次不能超過總次數', '횟수 한도가 올바르지 않아요. 계정당 횟수는 전체 횟수를 넘을 수 없어요', 'Invalid limits: per-user uses cannot exceed total uses'),
  invalid_expiry: e('有效期不合法：必須填寫，且晚於生效時間', '유효 기간이 올바르지 않아요. 필수이며 시작 시각보다 늦어야 해요', 'Invalid expiry: required and later than the start time'),
  invalid_amount: e('金額不合法：需為整數花瓣，且在允許範圍內', '금액이 올바르지 않아요. 정수 꽃잎이어야 하고 허용 범위 안이어야 해요', 'Invalid amount: whole petals within the allowed range'),
  invalid_pool: e('獎池設定不合法：檔位份數合計必須等於獎池容量', '상금 풀 설정이 올바르지 않아요. 구간 수량 합이 풀 크기와 같아야 해요', 'Invalid prize pool: tier counts must add up to the pool size'),
  invalid_status: e('狀態值不合法', '상태 값이 올바르지 않아요', 'Invalid status'),
  invalid_note: e('備註格式不合法', '메모 형식이 올바르지 않아요', 'Invalid note'),
  nothing_to_update: e('沒有可更新的內容，或這個碼不存在', '바꿀 내용이 없거나 코드가 없어요', 'Nothing to update, or the code does not exist'),
  empty_batch: e('沒有可解析的行：每行格式為「帳號 ID 或電子信箱, 花瓣金額, 事由」', '해석할 줄이 없어요. 각 줄은 「계정 ID 또는 이메일, 꽃잎 금액, 사유」 형식이에요', 'Nothing to parse: each line is account ID or email, amount, reason'),
  batch_too_large: e('一批最多 500 行', '한 번에 최대 500줄까지예요', 'At most 500 lines per batch'),
  batch_has_errors: e('有行存在問題，整批未執行', '문제가 있는 줄이 있어 전체를 실행하지 않았어요', 'Some lines have problems; nothing was executed'),
  ref_required: e('缺少請求號，請重新校驗後再執行', '요청 번호가 없어요. 다시 검증한 뒤 실행해 주세요', 'Missing reference; validate again before executing'),
  large_normal_grant_needs_ack: e('有償餘額的大額發放需要確認', '유상 잔액 고액 지급은 확인이 필요해요', 'Large paid-balance grants need confirmation'),
  bonus_deduct_unsupported: e('贈送花瓣不能扣減，請改扣有償餘額', '보너스 꽃잎은 차감할 수 없어요. 유상 잔액에서 차감해 주세요', 'Bonus petals cannot be deducted; deduct from paid balance'),
  control_unavailable: e('網關控制面暫時不可用，沒有做任何改動', '게이트웨이 제어 서비스에 연결할 수 없어 아무것도 바꾸지 않았어요', 'Gateway control is unavailable; nothing was changed'),
  control_sync_failed: e('帳號狀態已改，但網關側的停用狀態沒有同步成功，請重試一次', '계정 상태는 바뀌었지만 게이트웨이 쪽 정지 상태 동기화에 실패했어요. 한 번 더 시도해 주세요', 'Account changed but the gateway status failed to sync; retry once'),
  page_too_deep: e('合併列表最多翻到第 1000 條，請先按渠道篩選再翻頁', '병합 목록은 1000번째까지만 넘길 수 있어요. 채널로 먼저 좁혀 주세요', 'Merged lists page up to 1000 rows; filter by channel first'),
  invalid_pay_status: e('支付狀態篩選值不合法', '결제 상태 필터 값이 올바르지 않아요', 'Invalid payment status filter'),
  invalid_pay_channel: e('渠道篩選值不合法', '채널 필터 값이 올바르지 않아요', 'Invalid channel filter'),
  invalid_refund_status: e('退款狀態篩選值不合法', '환불 상태 필터 값이 올바르지 않아요', 'Invalid refund status filter'),
  invalid_org_id: e('帳號 ID 格式不合法', '계정 ID 형식이 올바르지 않아요', 'Invalid account ID'),
  invalid_channel: e('渠道取值不合法', '채널 값이 올바르지 않아요', 'Invalid channel'),
}

/** 本组页面的错误提示：先查本组新增的错误码，其余沿用共用错误表。 */
export function errorTextUsers(err: unknown, locale: Locale): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''
  const entry = CODES[code]
  return entry ? translate(entry, locale) : baseErrorText(err, locale)
}

/** 批量调账每行的错误码与警告码。 */
export const BATCH: Record<string, Entry> = {
  missing_amount: e('缺少金額', '금액이 없어요', 'Missing amount'),
  thousands_separator: e('金額含千分位逗號，請去掉逗號', '금액에 천 단위 쉼표가 있어요. 쉼표를 빼 주세요', 'Amount has a thousands separator; remove the comma'),
  bad_amount: e('金額格式不對', '금액 형식이 올바르지 않아요', 'Amount is not a number'),
  amount_not_integer: e('金額必須是整數花瓣', '금액은 정수 꽃잎이어야 해요', 'Amount must be whole petals'),
  amount_zero: e('金額不能為 0', '금액은 0일 수 없어요', 'Amount cannot be zero'),
  amount_too_large: e('金額超過單筆上限（1000 萬花瓣）', '금액이 건당 한도(1000만 꽃잎)를 넘었어요', 'Amount exceeds the per-entry limit (10,000,000 petals)'),
  user_not_found: e('找不到這個使用者', '사용자를 찾을 수 없어요', 'User not found'),
  bonus_deduct_unsupported: e('贈送花瓣不能扣減', '보너스 꽃잎은 차감할 수 없어요', 'Bonus petals cannot be deducted'),
  negative_balance: e('執行後餘額為負', '실행 후 잔액이 음수가 돼요', 'Balance will be negative after this line'),
}

export const AG_users = {
  common: {
    loading: e('載入中…', '불러오는 중이에요…', 'Loading…'),
    loadFailed: e('載入失敗：{msg}', '불러오지 못했어요: {msg}', 'Load failed: {msg}'),
    dash: e('—', '—', '—'),
    cancel: e('取消', '취소', 'Cancel'),
    confirm: e('確定', '확인', 'Confirm'),
    close: e('關閉', '닫기', 'Close'),
    refresh: e('刷新', '새로고침', 'Refresh'),
    filter: e('篩選', '필터', 'Filter'),
    clear: e('清空', '초기화', 'Clear'),
    copy: e('複製', '복사', 'Copy'),
    copied: e('已複製', '복사했어요', 'Copied'),
    copyFail: e('複製失敗，請手動選中複製', '복사하지 못했어요. 직접 선택해서 복사해 주세요', 'Copy failed; select and copy manually'),
    save: e('保存', '저장', 'Save'),
    reason: e('原因', '사유', 'Reason'),
    reasonHint: e('必填，2 到 500 字，會寫進審計記錄', '필수, 2~500자이며 감사 기록에 남아요', 'Required, 2 to 500 characters, kept in the audit log'),
    time: e('時間', '시간', 'Time'),
    status: e('狀態', '상태', 'Status'),
    note: e('備註', '메모', 'Note'),
    actions: e('操作', '작업', 'Actions'),
    petals: e('{n} 花瓣', '{n} 꽃잎', '{n} petals'),
    permanent: e('永久', '영구', 'Permanent'),
  },

  users: {
    eyebrow: e('USER OPERATIONS', 'USER OPERATIONS', 'USER OPERATIONS'),
    title: e('使用者與計費', '사용자와 과금', 'Users & billing'),
    desc: e('統一查看使用者資產、模型消耗、API Key 與帳號風險狀態', '사용자 자산, 모델 사용량, API Key와 계정 위험 상태를 한곳에서 봐요', 'User assets, model usage, API keys and account risk in one place'),
    chip: e('使用者資產實時視圖', '사용자 자산 실시간 보기', 'Live user assets'),
    batchBtn: e('批量調帳', '일괄 조정', 'Batch adjust'),
    accountsLink: e('帳號詳情', '계정 상세', 'Account detail'),
    searchPh: e('搜尋 ID / 暱稱 / 電子信箱 / Key 尾號', 'ID / 닉네임 / 이메일 / Key 끝자리로 검색', 'Search ID / name / email / key tail'),
    exactId: e('精確 ID', '정확한 ID', 'Exact ID'),
    exactIdHint: e('（只顯示 ID 完全相同的那一個使用者）', '(ID가 완전히 같은 한 명만 보여요)', '(only the one user with this exact ID)'),
    paidOnly: e('只看充過錢的', '충전한 사용자만', 'Paid users only'),
    keyHint: e('Key 後 3 到 8 位字母數字可以反查使用者，例如 {ex}', 'Key 끝 3~8자 영숫자로 사용자를 찾을 수 있어요. 예: {ex}', 'The last 3 to 8 letters or digits of a key find its user, e.g. {ex}'),
    colUser: e('使用者', '사용자', 'User'),
    colBalance: e('餘額', '잔액', 'Balance'),
    colCost: e('總消耗', '총 사용액', 'Total spend'),
    colRequests: e('請求數', '요청 수', 'Requests'),
    colTokens: e('Token', 'Token', 'Tokens'),
    colKeys: e('Keys', 'Keys', 'Keys'),
    empty: e('沒有匹配的使用者', '일치하는 사용자가 없어요', 'No matching users'),
    total: e('共 {n} 位使用者', '사용자 {n}명', '{n} users'),
    moreHint: e('每頁 {n} 位，按需翻頁', '페이지당 {n}명', '{n} per page'),
    badgeBanned: e('已封禁', '정지됨', 'Banned'),
    badgeInternal: e('內部', '내부', 'Internal'),
    badgeGeo: e('地域豁免', '지역 예외', 'Geo exempt'),
    badgePaid: e('已付費', '결제함', 'Paid'),
    noName: e('（未設定暱稱）', '(닉네임 없음)', '(no name)'),

    drawer: {
      label: e('使用者詳情', '사용자 상세', 'User detail'),
      balance: e('當前餘額', '현재 잔액', 'Balance'),
      tabOverview: e('概覽', '개요', 'Overview'),
      tabActivity: e('請求記錄', '요청 기록', 'Requests'),
      tabLedger: e('花瓣流水', '꽃잎 내역', 'Ledger'),
      tabAccount: e('帳戶與權限', '계정과 권한', 'Account'),
      usageStats: e('使用統計', '사용 통계', 'Usage'),
      totalCost: e('總消耗', '총 사용액', 'Total spend'),
      totalRequests: e('總請求', '총 요청', 'Requests'),
      inputTokens: e('輸入 Token', '입력 Token', 'Input tokens'),
      outputTokens: e('輸出 Token', '출력 Token', 'Output tokens'),
      trend: e('消耗趨勢', '사용 추이', 'Spend trend'),
      days: e('{n}天', '{n}일', '{n}d'),
      chartCost: e('每日消耗（花瓣）', '일별 사용액 (꽃잎)', 'Daily spend (petals)'),
      chartTokens: e('每日 Token', '일별 Token', 'Daily tokens'),
      chartRequests: e('每日請求數', '일별 요청 수', 'Daily requests'),
      inputLabel: e('輸入', '입력', 'Input'),
      outputLabel: e('輸出', '출력', 'Output'),
      byModel: e('按模型消耗', '모델별 사용', 'By model'),
      colModel: e('模型', '모델', 'Model'),
      colRequests: e('請求數', '요청 수', 'Requests'),
      colTokens: e('Token', 'Token', 'Tokens'),
      colCost: e('消耗', '사용액', 'Spend'),
      noData: e('暫無資料', '데이터가 없어요', 'No data yet'),
      recent: e('最近請求', '최근 요청', 'Recent requests'),
      colTime: e('時間', '시간', 'Time'),
      colStatus: e('狀態', '상태', 'Status'),
      colDuration: e('耗時', '소요', 'Duration'),
      colError: e('錯誤碼', '오류 코드', 'Error'),
      ledgerSummary: e('對帳匯總', '정산 요약', 'Reconciliation'),
      ledgerIn: e('累計入帳', '누적 입금', 'Total in'),
      ledgerOut: e('累計出帳', '누적 출금', 'Total out'),
      ledgerNet: e('流水淨額', '내역 순액', 'Net'),
      ledgerPaid: e('Stripe 儲值入帳', 'Stripe 충전 입금', 'Stripe top-ups'),
      ledgerPaidTip: e('點這裡只看資金流水', '자금 내역만 보려면 누르세요', 'Click to show money entries only'),
      unrecorded: e('錢包餘額與帳本淨額相差 {n} 花瓣，請核對帳本與錢包投影', '지갑 잔액과 원장 순액이 {n} 꽃잎 달라요. 원장과 지갑을 확인해 주세요', 'Wallet balance and ledger net differ by {n} petals; check the ledger and wallet'),
      ledgerDetail: e('流水明細', '내역 상세', 'Entries'),
      familyAll: e('全部', '전체', 'All'),
      familyMoney: e('資金', '자금', 'Money'),
      familyUsage: e('消耗', '사용', 'Usage'),
      count: e('共 {n} 條', '{n}건', '{n} entries'),
      colType: e('類型', '유형', 'Type'),
      colChannel: e('來源', '출처', 'Source'),
      colAmount: e('變動', '변동', 'Change'),
      colAfter: e('變動後餘額', '변동 후 잔액', 'Balance after'),
      colNote: e('備註', '메모', 'Note'),
      kinds: {
        usage: e('模型消耗', '모델 사용', 'Usage'),
        credit: e('儲值入帳', '충전 입금', 'Top-up'),
        bonus_grant: e('贈送花瓣', '보너스 꽃잎', 'Bonus grant'),
        adjustment: e('餘額調整', '잔액 조정', 'Adjustment'),
        refund_clawback: e('退款追回', '환불 회수', 'Refund clawback'),
      } as Record<string, Entry>,
      channels: {
        stripe: e('Stripe 支付', 'Stripe 결제', 'Stripe'),
        admin: e('管理員操作', '관리자 작업', 'Admin'),
        activation: e('啟用碼', '활성화 코드', 'Activation code'),
      } as Record<string, Entry>,
      adjustTitle: e('餘額調整（花瓣）', '잔액 조정 (꽃잎)', 'Adjust balance (petals)'),
      amountPh: e('花瓣數量', '꽃잎 수량', 'Petals'),
      notePh: e('備註', '메모', 'Note'),
      add: e('增加', '추가', 'Add'),
      deduct: e('扣減', '차감', 'Deduct'),
      asBound: e('發放為贈送花瓣', '보너스 꽃잎으로 지급', 'Grant as bonus petals'),
      asBoundHint: e('客服補償、活動補發時勾選；給合作方發放有償餘額時不勾。贈送花瓣只能增加', '고객 보상·이벤트 재지급 때 선택해요. 파트너에게 유상 잔액을 줄 때는 선택하지 않아요. 보너스는 추가만 돼요', 'Tick for support compensation or campaign top-ups; leave unticked for paid balance. Bonus can only be added'),
      amountInvalid: e('請填寫大於 0 的整數花瓣', '0보다 큰 정수 꽃잎을 입력해 주세요', 'Enter a whole number of petals above 0'),
      adjustDone: e('已調整，當前餘額 {v} 花瓣', '조정했어요. 현재 잔액 {v} 꽃잎', 'Adjusted. Balance is now {v} petals'),
      adjustRepeat: e('這次提交之前已經記過，餘額沒有重複變動', '이 요청은 이미 기록돼 있어 잔액이 다시 바뀌지 않았어요', 'This submission was already recorded; balance unchanged'),
      largeTitle: e('確認大額發放', '고액 지급 확인', 'Confirm large grant'),
      largeBody: e('這筆有償餘額發放超過 10,000 花瓣，確認繼續嗎？', '이 유상 잔액 지급은 10,000 꽃잎을 넘어요. 계속할까요?', 'This paid-balance grant exceeds 10,000 petals. Continue?'),
      keys: e('API Keys', 'API Keys', 'API Keys'),
      keysEmpty: e('這個使用者還沒有 Key', '이 사용자는 아직 Key가 없어요', 'This user has no keys yet'),
      keyRevoked: e('已吊銷', '폐기됨', 'Revoked'),
      keyQuota: e('額度 {used} / {limit} 花瓣', '한도 {used} / {limit} 꽃잎', 'Quota {used} / {limit} petals'),
      keyUnlimited: e('無額度上限', '한도 없음', 'No quota limit'),
      keyLastUsed: e('最近使用 {t}', '최근 사용 {t}', 'Last used {t}'),
      keyNever: e('從未使用', '사용 기록 없음', 'Never used'),
      controls: e('帳號管控', '계정 관리', 'Account controls'),
      accountStatus: e('帳號狀態', '계정 상태', 'Account status'),
      statusActive: e('正常', '정상', 'Active'),
      statusBanned: e('已封禁', '정지됨', 'Banned'),
      ban: e('封禁', '정지', 'Ban'),
      unban: e('解封', '해제', 'Unban'),
      memberType: e('成員類型', '구성원 유형', 'Member type'),
      typeInternal: e('內部帳號（團隊成員自用）', '내부 계정 (팀 구성원)', 'Internal account (team member)'),
      typeExternal: e('外部使用者', '외부 사용자', 'External user'),
      markInternal: e('標記為內部', '내부로 표시', 'Mark internal'),
      unmarkInternal: e('取消內部標記', '내부 표시 해제', 'Unmark internal'),
      geo: e('地域豁免', '지역 예외', 'Geo exemption'),
      geoOn: e('已豁免：受限模型不做地域判定', '예외 적용: 제한 모델의 지역 판정을 건너뛰어요', 'Exempt: region checks are skipped'),
      geoOff: e('未豁免', '예외 아님', 'Not exempt'),
      geoEnable: e('開啟豁免', '예외 켜기', 'Exempt'),
      geoDisable: e('關閉豁免', '예외 끄기', 'Remove exemption'),
      accountType: e('帳號類型', '계정 유형', 'Account type'),
      accountTypeValue: e('帳號（餘額與 Key 直接掛在帳號上）', '계정 (잔액과 Key는 계정에 바로 있어요)', 'Account (balance and keys belong to it directly)'),
      flagNote: e('內部標記與地域豁免已記錄，資料面還沒有對應開關，目前只作標記與審計', '내부 표시와 지역 예외는 기록만 되며 데이터 영역에 아직 연결된 스위치가 없어요', 'Internal and geo flags are recorded; the data plane has no switch for them yet'),
      banHistory: e('封禁歷史', '정지 기록', 'Ban history'),
      banHistoryEmpty: e('沒有封禁記錄', '정지 기록이 없어요', 'No ban records'),
      banActive: e('生效中', '적용 중', 'Active'),
      banLifted: e('已解除', '해제됨', 'Lifted'),
      banBy: e('操作人 {id}', '처리자 {id}', 'By {id}'),
      banLiftReason: e('解封原因：{r}', '해제 사유: {r}', 'Lift reason: {r}'),
      banTitle: e('封禁 {who}', '{who} 정지', 'Ban {who}'),
      banBody: e('封禁會鎖定帳號、作廢所有會話，名下的 Key 立即不能呼叫。', '정지하면 계정이 잠기고 모든 세션이 끊기며, 계정의 Key 호출이 즉시 막혀요.', 'Banning locks the account and ends all sessions; its keys stop working immediately.'),
      unbanTitle: e('解封 {who}', '{who} 정지 해제', 'Unban {who}'),
      unbanBody: e('解封會恢復帳號。', '해제하면 계정이 복구돼요.', 'Unbanning restores the account.'),
      internalTitle: e('{who} 的成員類型', '{who}의 구성원 유형', 'Member type of {who}'),
      geoTitle: e('{who} 的地域豁免', '{who}의 지역 예외', 'Geo exemption of {who}'),
      changed: e('已更新', '반영했어요', 'Updated'),
    },

    batch: {
      title: e('批量調帳', '일괄 조정', 'Batch adjust'),
      format: e('每行一條，格式「帳號 ID 或電子信箱, 金額, 事由」', '한 줄에 하나씩 「계정 ID 또는 이메일, 금액, 사유」', 'One per line: account ID or email, amount, reason'),
      formatHint: e('金額單位花瓣，負數為扣減；事由可省略；# 開頭的行忽略', '금액 단위는 꽃잎이며 음수는 차감이에요. 사유는 생략할 수 있고 #으로 시작하는 줄은 무시해요', 'Amounts in petals, negative deducts; reason optional; lines starting with # are ignored'),
      placeholder: e('<帳號 ID>, 5000, 活動補償\nname@example.com, -200, 違規扣回\n<帳號 ID>, 10000', '<계정 ID>, 5000, 이벤트 보상\nname@example.com, -200, 위반 회수\n<계정 ID>, 10000', '<account ID>, 5000, campaign top-up\nname@example.com, -200, violation clawback\n<account ID>, 10000'),
      asBound: e('整批發放為贈送花瓣', '전체를 보너스 꽃잎으로 지급', 'Grant the whole batch as bonus petals'),
      asBoundHint: e('改動後需要重新校驗', '바꾸면 다시 검증해야 해요', 'Validate again after changing'),
      preview: e('校驗並預覽', '검증하고 미리보기', 'Validate & preview'),
      execute: e('確認執行', '실행 확정', 'Execute'),
      summary: e('{ok} 行可執行，{errors} 行有問題，涉及 {users} 位使用者；增加 {add}、扣減 {sub}、淨額 {net} 花瓣', '실행 가능 {ok}줄, 문제 {errors}줄, 사용자 {users}명. 추가 {add}, 차감 {sub}, 순액 {net} 꽃잎', '{ok} lines ready, {errors} with problems, {users} users; add {add}, deduct {sub}, net {net} petals'),
      warnings: e('其中 {n} 行執行後餘額為負', '{n}줄은 실행 후 잔액이 음수예요', '{n} lines leave a negative balance'),
      colLine: e('行', '줄', 'Line'),
      colTarget: e('使用者', '사용자', 'User'),
      colAmount: e('金額', '금액', 'Amount'),
      colBefore: e('調整前', '조정 전', 'Before'),
      colAfter: e('調整後', '조정 후', 'After'),
      colNote: e('事由', '사유', 'Reason'),
      colResult: e('結果', '결과', 'Result'),
      ok: e('可執行', '실행 가능', 'Ready'),
      done: e('已執行：{n} 行，淨額 {net} 花瓣', '실행했어요: {n}줄, 순액 {net} 꽃잎', 'Executed: {n} lines, net {net} petals'),
      largeConfirm: e('其中 {n} 行是超過 10,000 花瓣的有償餘額發放，確認後才能執行', '{n}줄이 10,000 꽃잎을 넘는 유상 잔액 지급이에요. 확인해야 실행돼요', '{n} lines are paid-balance grants above 10,000 petals; confirm to execute'),
      largeCheck: e('我確認這些大額發放', '이 고액 지급을 확인했어요', 'I confirm these large grants'),
      empty: e('請先填寫內容', '내용을 먼저 입력해 주세요', 'Enter some lines first'),
    },
  },

  orders: {
    eyebrow: e('PAYMENT OPERATIONS', 'PAYMENT OPERATIONS', 'PAYMENT OPERATIONS'),
    title: e('儲值訂單', '충전 주문', 'Recharge orders'),
    desc: e('聚焦真實收款、支付狀態與退款處理進度', '실제 결제, 결제 상태와 환불 진행을 중심으로 봐요', 'Real payments, payment status and refund progress'),
    chip: e('Stripe 支付帳務視圖', 'Stripe 결제 회계 보기', 'Stripe payment ledger view'),
    statPaid: e('已支付總額', '결제 완료 합계', 'Total paid'),
    statPending: e('待支付訂單', '결제 대기 주문', 'Pending orders'),
    statRefunded: e('已退款總額', '환불 합계', 'Total refunded'),
    paidSub: e('已扣除退款，{n} 筆訂單', '환불 제외, 주문 {n}건', 'Net of refunds, {n} orders'),
    refundedSub: e('{n} 筆訂單有退款', '주문 {n}건에 환불이 있어요', '{n} orders with refunds'),
    searchPh: e('搜尋使用者 / 訂單號 / 交易號…', '사용자 / 주문번호 / 거래번호 검색…', 'Search user / order ID / transaction ID…'),
    channelAll: e('全部渠道（含非收款記錄）', '전체 채널 (비결제 기록 포함)', 'All channels (incl. non-payment records)'),
    channelStripe: e('Stripe 銀行卡（真實收款）', 'Stripe 카드 (실제 결제)', 'Stripe card (real payments)'),
    channelAdmin: e('僅管理員手動調整', '관리자 수동 조정만', 'Admin adjustments only'),
    channelActivation: e('僅啟用碼到帳', '활성화 코드 입금만', 'Activation codes only'),
    channelTip: e('預設展示全部渠道；只看真實收款時切到 Stripe', '기본은 전체 채널이에요. 실제 결제만 보려면 Stripe를 고르세요', 'All channels by default; pick Stripe for real payments only'),
    statusAll: e('全部狀態', '전체 상태', 'All statuses'),
    statusPaid: e('已支付', '결제 완료', 'Paid'),
    statusPending: e('待支付', '결제 대기', 'Pending'),
    statusClosed: e('已關閉', '종료됨', 'Closed'),
    sourceAll: e('全部入口', '전체 진입점', 'All entries'),
    sourceConsole: e('控制台', '콘솔', 'Console'),
    sourceOther: e('其他', '기타', 'Other'),
    sourceTip: e('下單時所在的入口，與支付渠道是兩個維度', '주문한 진입점이며 결제 채널과는 별개예요', 'Where the order was placed; independent of the payment channel'),
    refundAll: e('全部退款狀態', '전체 환불 상태', 'All refund states'),
    refundNone: e('無退款', '환불 없음', 'No refund'),
    refundPartial: e('部分退款', '부분 환불', 'Partial refund'),
    refundFull: e('已全額退款', '전액 환불', 'Fully refunded'),
    refundPending: e('退款處理中', '환불 처리 중', 'Refund in progress'),
    total: e('共 {n} 條', '{n}건', '{n} records'),
    note: e(
      '預設展示全部渠道，包括 Stripe 銀行卡的真實收款，以及管理員調帳、啟用碼到帳等餘額變動流水；後者沒有真實收款。只看收款時把渠道切到「Stripe 銀行卡」。使用者稱已付款但仍顯示「待支付」時，先點該訂單的「同步 Stripe」，不要直接手動增加餘額；系統後台也會按退避策略持續自動補單。列表按時間合併兩類來源，翻頁最多到第 1000 條。',
      '기본으로 전체 채널을 보여줘요. Stripe 카드의 실제 결제와 함께 관리자 조정, 활성화 코드 입금 같은 잔액 변동 내역이 포함되며 후자는 실제 결제가 아니에요. 결제만 보려면 채널을 「Stripe 카드」로 바꾸세요. 결제했다는데 「결제 대기」로 남아 있으면 먼저 「Stripe 동기화」를 누르고 잔액을 직접 늘리지 마세요. 시스템도 백오프 정책으로 계속 자동 보정해요. 두 출처를 시간순으로 합치며 1000번째까지만 넘길 수 있어요.',
      'All channels are shown by default: real Stripe card payments plus balance movements such as admin adjustments and activation codes, which carry no real payment. Switch the channel to Stripe card for payments only. If a user says they paid but the order is still pending, press Sync Stripe on that order first instead of adding balance by hand; the backend also retries on a backoff schedule. Both sources are merged by time and paging stops at row 1000.',
    ),
    lookupFailed: e('帳號服務暫時不可用，使用者電子信箱沒有顯示出來', '계정 서비스에 연결할 수 없어 사용자 이메일이 비어 있어요', 'The account service is unavailable, so user emails are missing'),
    colTime: e('下單時間', '주문 시각', 'Time'),
    colUser: e('使用者', '사용자', 'User'),
    colOrder: e('訂單號', '주문번호', 'Order'),
    colAmount: e('金額', '금액', 'Amount'),
    colChannel: e('渠道', '채널', 'Channel'),
    colSource: e('入口', '진입점', 'Entry'),
    colStatus: e('支付狀態', '결제 상태', 'Payment'),
    colTxn: e('Stripe 交易號', 'Stripe 거래번호', 'Stripe transaction'),
    colRefund: e('退款狀態', '환불 상태', 'Refund'),
    colActions: e('操作', '작업', 'Actions'),
    channelNames: {
      stripe: e('Stripe 銀行卡', 'Stripe 카드', 'Stripe card'),
      admin: e('管理員調整', '관리자 조정', 'Admin adjustment'),
      activation: e('啟用碼', '활성화 코드', 'Activation code'),
    } as Record<string, Entry>,
    grantKinds: {
      credit: e('入帳', '입금', 'Credit'),
      bonus_grant: e('贈送', '보너스', 'Bonus'),
      adjustment: e('調整', '조정', 'Adjustment'),
      refund_clawback: e('追回', '회수', 'Clawback'),
    } as Record<string, Entry>,
    petalsPaid: e('{n} 花瓣', '{n} 꽃잎', '{n} petals'),
    petalsBonus: e('含贈送 {n}', '보너스 {n} 포함', 'incl. {n} bonus'),
    refundedOf: e('已退 {v}', '환불 {v}', 'Refunded {v}'),
    refundAt: e('退款於 {t}', '{t}에 환불', 'Refunded {t}'),
    notRealPayment: e('非真實收款', '실제 결제 아님', 'Not a real payment'),
    empty: e('沒有匹配的記錄', '일치하는 기록이 없어요', 'No matching records'),
    syncBtn: e('同步 Stripe', 'Stripe 동기화', 'Sync Stripe'),
    synced: e('已向 Stripe 回查並同步', 'Stripe에서 다시 조회해 동기화했어요', 'Re-checked with Stripe and synced'),
    refundBtn: e('退款', '환불', 'Refund'),
    detailBtn: e('詳情', '상세', 'Detail'),
    refundTitle: e('發起退款', '환불 요청', 'Issue a refund'),
    refundInfo: e('訂單 {id}，金額 {amount}，已退 {refunded}', '주문 {id}, 금액 {amount}, 환불 {refunded}', 'Order {id}, amount {amount}, refunded {refunded}'),
    refundWarn: e('退款原路退回銀行卡，並按比例追回使用者的花瓣餘額，餘額可能變負，操作不可撤銷。', '환불은 카드로 되돌려지고 사용자의 꽃잎 잔액이 비율대로 회수돼요. 잔액이 음수가 될 수 있고 되돌릴 수 없어요.', 'The refund goes back to the card and petals are clawed back pro rata; the balance may go negative and this cannot be undone.'),
    refundAmount: e('退款金額', '환불 금액', 'Refund amount'),
    refundMax: e('最多可退 {v}', '최대 {v}까지 환불할 수 있어요', 'At most {v} can be refunded'),
    refundDone: e('已發起退款', '환불을 요청했어요', 'Refund issued'),
    refundProgress: e('退款進度', '환불 진행', 'Refund progress'),
    refundPendingClaw: e('{n} 筆退款的花瓣追回還沒完成，對帳任務會繼續重試', '환불 {n}건의 꽃잎 회수가 아직 끝나지 않았어요. 정산 작업이 계속 재시도해요', '{n} refunds have petals still to claw back; reconciliation keeps retrying'),
    refundNoRecords: e('這張訂單沒有退款記錄', '이 주문은 환불 기록이 없어요', 'No refund records for this order'),
    refundColAmount: e('金額', '금액', 'Amount'),
    refundColStatus: e('狀態', '상태', 'Status'),
    refundColClaw: e('追回', '회수', 'Clawback'),
    clawed: e('已追回', '회수함', 'Clawed'),
    clawPending: e('待追回', '회수 대기', 'Pending'),
    productsTitle: e('儲值商品', '충전 상품', 'Products'),
    productsCopy: e('儲值頁展示的商品與到帳花瓣；改價只影響之後的訂單', '충전 페이지의 상품과 입금 꽃잎이에요. 가격 변경은 이후 주문에만 적용돼요', 'Products shown on the top-up page; price changes affect later orders only'),
    reconcileTitle: e('每日對帳', '일일 정산', 'Daily reconciliation'),
    reconcileCopy: e('支付通道流水與本地訂單的比對結果，以及卡住的訂單', '결제 채널 내역과 로컬 주문의 비교 결과와 멈춘 주문이에요', 'Channel ledger against local orders, plus stuck orders'),
    expand: e('展開', '펼치기', 'Expand'),
    collapse: e('收起', '접기', 'Collapse'),
  },

  codes: {
    eyebrow: e('GROWTH ASSETS', 'GROWTH ASSETS', 'GROWTH ASSETS'),
    title: e('啟用碼', '활성화 코드', 'Activation codes'),
    desc: e('管理啟用碼批次、兌換額度、有效期與傳播風險', '활성화 코드 배치, 지급 한도, 유효 기간과 확산 위험을 관리해요', 'Manage batches, redeem amounts, expiry and spread risk'),
    chip: e('增長資產管理', '성장 자산 관리', 'Growth asset management'),
    statTotal: e('啟用碼總數', '활성화 코드 수', 'Codes'),
    statValue: e('總價值', '총 가치', 'Total value'),
    statValueSub: e('面額 × 總次數上限；獎池碼按池子總額', '액면 × 총 횟수 한도, 상금 풀 코드는 풀 총액', 'Face value × total uses; prize pools by pool total'),
    statRedeemed: e('已兌換金額', '지급된 금액', 'Redeemed'),
    statUsers: e('兌換人數', '사용 인원', 'Redeemers'),
    statCountSub: e('共兌換 {n} 次', '총 {n}회 사용', '{n} redemptions'),
    searchPh: e('搜尋碼值 / 備註…', '코드 / 메모 검색…', 'Search code / note…'),
    statusAll: e('全部狀態', '전체 상태', 'All statuses'),
    statusActive: e('啟用中', '사용 중', 'Active'),
    statusDisabled: e('已禁用', '비활성', 'Disabled'),
    batchPh: e('批次號…', '배치 번호…', 'Batch ID…'),
    exportBatch: e('匯出批次 CSV', '배치 CSV 내보내기', 'Export batch CSV'),
    exportNeedBatch: e('先在批次號裡填一個批次，再匯出', '내보낼 배치 번호를 먼저 입력해 주세요', 'Enter a batch ID first'),
    blocksBtn: e('封禁名單', '차단 목록', 'Block list'),
    generate: e('生成啟用碼', '활성화 코드 생성', 'Generate codes'),
    colCode: e('碼值', '코드', 'Code'),
    colAmount: e('面額', '액면', 'Amount'),
    colUsed: e('已用 / 上限', '사용 / 한도', 'Used / limit'),
    colPerUser: e('單人限次', '1인 한도', 'Per user'),
    colRedeemed: e('已兌金額', '지급 금액', 'Redeemed'),
    colExpires: e('有效期至', '유효 기간', 'Expires'),
    colStatus: e('狀態', '상태', 'Status'),
    colBatch: e('批次', '배치', 'Batch'),
    colNote: e('備註', '메모', 'Note'),
    colActions: e('操作', '작업', 'Actions'),
    lottery: e('抽獎', '추첨', 'Lottery'),
    pool: e('獎池', '상금 풀', 'Prize pool'),
    empty: e('沒有匹配的啟用碼', '일치하는 코드가 없어요', 'No matching codes'),
    expired: e('已過期', '만료됨', 'Expired'),
    detailBtn: e('詳情', '상세', 'Detail'),
    disableBtn: e('禁用', '비활성화', 'Disable'),
    enableBtn: e('啟用', '활성화', 'Enable'),
    deleteBtn: e('刪除', '삭제', 'Delete'),
    total: e('共 {n} 個', '{n}개', '{n} codes'),
    batchTitle: e('批次統計', '배치 통계', 'Batch stats'),
    batchCopy: e('按發行批次匯總額度、兌換進度與使用效率', '발행 배치별 한도, 지급 진행과 사용 효율을 모아 봐요', 'Amounts, redemption progress and efficiency per batch'),
    batchCols: {
      batch: e('批次', '배치', 'Batch'),
      note: e('備註', '메모', 'Note'),
      codes: e('碼數', '코드 수', 'Codes'),
      value: e('總價值', '총 가치', 'Value'),
      redeemed: e('已兌換', '지급됨', 'Redeemed'),
      count: e('兌換次數', '사용 횟수', 'Uses'),
      actions: e('操作', '작업', 'Actions'),
    },
    batchDisabled: e('{n} 個已禁用', '{n}개 비활성', '{n} disabled'),
    batchViewCodes: e('查看碼', '코드 보기', 'View codes'),
    batchExport: e('匯出', '내보내기', 'Export'),
    batchDisableAll: e('整批禁用', '전체 비활성화', 'Disable all'),
    batchEnableAll: e('整批啟用', '전체 활성화', 'Enable all'),
    batchEditNote: e('改備註', '메모 수정', 'Edit note'),
    batchDelete: e('刪除批次', '배치 삭제', 'Delete batch'),
    batchNoteTitle: e('修改批次備註', '배치 메모 수정', 'Edit batch note'),
    batchNoteDone: e('已更新 {n} 個碼的備註', '코드 {n}개의 메모를 바꿨어요', 'Updated the note of {n} codes'),
    batchToggled: e('{n} 個碼已{act}', '코드 {n}개를 {act}했어요', '{n} codes {act}'),
    actDisabled: e('禁用', '비활성화', 'disabled'),
    actEnabled: e('啟用', '활성화', 'enabled'),
    deleteConfirmCode: e('刪除碼 {code}？它的 {n} 條兌換記錄會一併刪除，已到帳的花瓣不會收回。', '코드 {code}를 삭제할까요? 사용 기록 {n}건도 함께 삭제되며 이미 지급된 꽃잎은 회수되지 않아요.', 'Delete code {code}? Its {n} redemption records go too; credited petals stay.'),
    deleteConfirmBatch: e('刪除批次 {batch} 的全部 {n} 個碼？兌換記錄會一併刪除，已到帳的花瓣不會收回，不可撤銷。', '배치 {batch}의 코드 {n}개를 모두 삭제할까요? 사용 기록도 삭제되고 지급된 꽃잎은 회수되지 않으며 되돌릴 수 없어요.', 'Delete all {n} codes of batch {batch}? Redemption records go too, credited petals stay, and this cannot be undone.'),
    deleted: e('已刪除', '삭제했어요', 'Deleted'),

    gen: {
      title: e('生成啟用碼', '활성화 코드 생성', 'Generate codes'),
      modeBatch: e('批量機器生成', '일괄 자동 생성', 'Batch'),
      modeCustom: e('自定義碼', '사용자 지정 코드', 'Custom code'),
      count: e('生成數量（1~10000）', '생성 수량 (1~10000)', 'Count (1 to 10000)'),
      customCode: e('自定義碼值（大寫字母 + 數字，4~32 位）', '사용자 지정 코드 (대문자+숫자, 4~32자)', 'Custom code (uppercase + digits, 4 to 32)'),
      customPh: e('如 SPRING666', '예: SPRING666', 'e.g. SPRING666'),
      typeFixed: e('固定面額', '고정 액면', 'Fixed amount'),
      typeLottery: e('抽獎（區間隨機）', '추첨 (구간 랜덤)', 'Lottery (random range)'),
      typePool: e('獎池（分布可控）', '상금 풀 (분포 지정)', 'Prize pool (set distribution)'),
      typePoolTip: e('建立時把每一份獎金拆好並洗牌，兌換時按序取走；總額確定，分檔表可以直接當獎級表公示', '생성할 때 모든 몫을 나눠 섞어 두고 사용 순서대로 가져가요. 총액이 정해지고 구간표를 그대로 공개할 수 있어요', 'Every share is split and shuffled at creation and handed out in order; the total is fixed and the tier table can be published'),
      amount: e('面額（花瓣）', '액면 (꽃잎)', 'Amount (petals)'),
      amountLow: e('抽獎下限（花瓣）', '추첨 하한 (꽃잎)', 'Lottery minimum (petals)'),
      amountMax: e('抽獎上限（花瓣）', '추첨 상한 (꽃잎)', 'Lottery maximum (petals)'),
      maxTotal: e('總次數上限', '총 횟수 한도', 'Total uses'),
      maxPerUser: e('單帳號限次', '계정당 한도', 'Uses per account'),
      expires: e('有效期至', '유효 기간', 'Expires at'),
      validFrom: e('生效時間（可選）', '시작 시각 (선택)', 'Valid from (optional)'),
      note: e('備註（如活動名）', '메모 (예: 이벤트 이름)', 'Note (e.g. campaign)'),
      notePh: e('如：春季內測活動', '예: 봄 베타 이벤트', 'e.g. spring beta'),
      previewFixed: e('將生成 {count} 個碼，每個 {amount} 花瓣，最多可兌換 {total} 花瓣', '코드 {count}개를 만들며 개당 {amount} 꽃잎, 최대 {total} 꽃잎이 지급돼요', '{count} codes at {amount} petals each, up to {total} petals'),
      previewLottery: e('將生成 {count} 個碼，每次隨機 {low} 到 {high} 花瓣，最壞預算 {total} 花瓣', '코드 {count}개를 만들며 매번 {low}~{high} 꽃잎 랜덤, 최대 예산 {total} 꽃잎', '{count} codes, each redemption random {low} to {high} petals, worst case {total} petals'),
      previewPool: e('獎池容量 {size} 份，總額 {total} 花瓣', '풀 크기 {size}몫, 총액 {total} 꽃잎', 'Pool of {size} shares totalling {total} petals'),
      submit: e('生成', '생성', 'Generate'),
      resultTitle: e('生成成功', '생성했어요', 'Generated'),
      resultSummary: e('批次 {batch}，共 {n} 個碼', '배치 {batch}, 코드 {n}개', 'Batch {batch}, {n} codes'),
      copyAll: e('複製全部碼值', '코드 전체 복사', 'Copy all codes'),
      copyShare: e('複製分享文案', '공유 문구 복사', 'Copy share text'),
      done: e('完成', '완료', 'Done'),
      shareText: e('兌換碼：{code}\n有效期至 {date}', '쿠폰 코드: {code}\n유효 기간 {date}', 'Code: {code}\nValid until {date}'),
      customDone: e('已建立碼 {code}', '코드 {code}를 만들었어요', 'Created code {code}'),
      validFromBefore: e('生效時間必須早於有效期', '시작 시각은 유효 기간보다 빨라야 해요', 'The start time must be earlier than the expiry'),
      expiresRequired: e('請選擇有效期', '유효 기간을 선택해 주세요', 'Choose an expiry'),
      amountRequired: e('請填寫花瓣數量（整數，至少 1）', '꽃잎 수량을 입력해 주세요 (정수, 1 이상)', 'Enter whole petals, at least 1'),
      lotteryRange: e('抽獎上限必須大於下限', '추첨 상한은 하한보다 커야 해요', 'The maximum must exceed the minimum'),
      poolSize: e('獎池份數', '풀 크기', 'Pool size'),
      poolSizeHint: e('= 碼數量 × 總次數上限', '= 코드 수 × 총 횟수 한도', '= codes × total uses'),
      poolAdd: e('多一檔', '구간 추가', 'Add tier'),
      poolRemove: e('少一檔', '구간 삭제', 'Remove tier'),
      poolTierAmount: e('面額（花瓣）', '액면 (꽃잎)', 'Amount (petals)'),
      poolTierCount: e('份數', '몫 수', 'Shares'),
      poolTierShare: e('佔比', '비율', 'Share'),
      poolTierTotal: e('小計（花瓣）', '소계 (꽃잎)', 'Subtotal (petals)'),
      poolMismatch: e('份數合計 {have}，需要等於獎池容量 {need}', '몫 합계 {have}, 풀 크기 {need}와 같아야 해요', 'Shares add up to {have}; must equal the pool size {need}'),
      poolMetrics: e('平均 {avg} 花瓣，最低 {min}，最高 {max}，總額 {total}', '평균 {avg} 꽃잎, 최저 {min}, 최고 {max}, 총액 {total}', 'Average {avg} petals, min {min}, max {max}, total {total}'),
      poolNoShape: e('獎池在建立時就確定，之後不能改分布', '상금 풀은 생성할 때 정해지며 이후 분포를 바꿀 수 없어요', 'The pool is fixed at creation; the distribution cannot change later'),
    },

    detail: {
      title: e('啟用碼詳情', '활성화 코드 상세', 'Code detail'),
      code: e('碼值', '코드', 'Code'),
      batch: e('批次', '배치', 'Batch'),
      amount: e('面額', '액면', 'Amount'),
      uses: e('已用 / 上限', '사용 / 한도', 'Used / limit'),
      perUser: e('單帳號限次', '계정당 한도', 'Per account'),
      validFrom: e('生效時間', '시작 시각', 'Valid from'),
      expires: e('有效期至', '유효 기간', 'Expires'),
      status: e('狀態', '상태', 'Status'),
      redeemed: e('已兌金額', '지급 금액', 'Redeemed'),
      createdBy: e('建立人', '생성자', 'Created by'),
      createdAt: e('建立時間', '생성 시각', 'Created'),
      note: e('備註', '메모', 'Note'),
      edit: e('修改', '수정', 'Edit'),
      editExpiry: e('延長有效期', '유효 기간 변경', 'Change expiry'),
      editNote: e('修改備註', '메모 수정', 'Edit note'),
      editLimits: e('修改次數', '횟수 수정', 'Edit limits'),
      saved: e('已保存', '저장했어요', 'Saved'),
      poolTitle: e('獎池分布', '상금 풀 분포', 'Prize pool'),
      poolSummary: e('{claimed} / {size} 份已領，已發 {paid} / {total} 花瓣', '{claimed} / {size}몫 사용, {paid} / {total} 꽃잎 지급', '{claimed} / {size} claimed, {paid} / {total} petals paid'),
      poolColAmount: e('面額', '액면', 'Amount'),
      poolColCount: e('份數', '몫 수', 'Shares'),
      poolColClaimed: e('已領', '사용', 'Claimed'),
      redemptions: e('兌換記錄', '사용 기록', 'Redemptions'),
      colTime: e('時間', '시간', 'Time'),
      colUser: e('使用者', '사용자', 'User'),
      colAccount: e('帳號', '계정', 'Account'),
      colAmount: e('金額', '금액', 'Amount'),
      colBalance: e('兌換後餘額', '지급 후 잔액', 'Balance after'),
      noRedemptions: e('還沒有兌換記錄', '아직 사용 기록이 없어요', 'No redemptions yet'),
    },

    blocks: {
      title: e('兌換封禁名單', '코드 사용 차단 목록', 'Redemption block list'),
      desc: e('連續輸錯啟用碼的帳號會被階梯封禁：連續 3 次失敗封 1 小時，之後依次 3 小時、24 小時、48 小時、7 天、30 天；30 天內沒有失敗，計數與等級清零。', '활성화 코드를 연속으로 틀린 계정은 단계적으로 차단돼요. 3회 연속 실패 시 1시간, 이후 3시간, 24시간, 48시간, 7일, 30일이며 30일 동안 실패가 없으면 횟수와 단계가 초기화돼요.', 'Accounts that keep entering wrong codes are blocked in steps: 3 failures in a row block for 1 hour, then 3 hours, 24 hours, 48 hours, 7 days and 30 days; 30 days without failures reset the count and level.'),
      colUser: e('使用者', '사용자', 'User'),
      colLevel: e('封禁等級', '차단 단계', 'Level'),
      colUntil: e('解封時間', '해제 시각', 'Unblocks at'),
      colActions: e('操作', '작업', 'Actions'),
      unblock: e('解封', '해제', 'Unblock'),
      unblockReset: e('解封並清零等級', '해제하고 단계 초기화', 'Unblock and reset level'),
      empty: e('當前沒有被封禁的帳號', '차단된 계정이 없어요', 'No blocked accounts'),
      unblocked: e('已解封', '해제했어요', 'Unblocked'),
    },
  },
}
